import { first } from 'rxjs/operators';

import { LoggerService, SdkMessageHandler, WCH_SDK_MODULE_NAME, WchSdk } from './../../../../api';
import { createMessageHandler, FUNCTION_TYPE, Generator } from './../../../../utils';
import { li, lw } from './../../logger/logger.service';
import { WchService } from './../../wch.service';
import { SdkJsonp, SdkJsonpDependencies } from './jsonp/jsonp';
import { SdkRouter, SdkRouterDependencies } from './router/router';
import { SdkSearch, SdkSearchDependencies } from './search/search';
import { SDK_VERSION, SdkVersion } from './version';

/* Copyright IBM Corp. 2017 */
const _MESSAGE_EVENT = 'message';

const bHasHandlers = (typeof addEventListener === FUNCTION_TYPE) && (typeof removeEventListener === FUNCTION_TYPE);

export interface SdkDependencies extends SdkRouterDependencies, SdkJsonpDependencies, SdkSearchDependencies {

    loggerService: LoggerService;

    wchService: WchService;

    msgHandlers: Generator<SdkMessageHandler[]>;
}

const LOGGER = 'Sdk';

const _MODULE_NAME = WCH_SDK_MODULE_NAME;

export class Sdk implements WchSdk {

    /*
     * Identifier of the SDK module
     */
    public static readonly MODULE_NAME = _MODULE_NAME;

    // public methods

    /*
     * Refreshes the rendering context and all JSON data that is currently displayed
     */
    refresh: () => void;
    /*
     * Performs cleanup on the service, only to be used during shutdown
     */
    destroy: () => void;

    private readonly sdkRouter: SdkRouter;

    private readonly sdkJsonp: SdkJsonp;

    private readonly sdkLogger: LoggerService;

    private readonly sdkSearch: SdkSearch;

    constructor(aDeps: SdkDependencies) {
        // init
        const that = this;
        // construct
        const sdkRouter = this.sdkRouter = new SdkRouter(aDeps);
        const sdkJsonp = this.sdkJsonp = new SdkJsonp(aDeps);
        const sdkSearch = this.sdkSearch = new SdkSearch(aDeps);
        const wchService = aDeps.wchService;
        const msgHandlers = aDeps.msgHandlers;

        const loggerService = this.sdkLogger = aDeps.loggerService;
        const logger = loggerService.get(LOGGER);

        // the whitelist
        let corsWhitelist: Promise<string[]>;

        /*
         * Returns the whitelist
         *
         * @returns a promise for the whitelist, will request the list when first asked
         */
        function _getCorsWhitelist(): Promise<string[]> {
            return corsWhitelist || (corsWhitelist = wchService.getCorsWhitelist().pipe(
                first()
            ).toPromise());
        }

        /*
         * Executes the edit mode event
         *
         * @param aPayload  the payload
         * @param aEvent    the event
         */
        /*function _onEditMode(aPayload: SdkEditModeEvent, aEvent: MessageEvent) {
            // log
            li(LOGGER, 'onEditMode', aPayload);
            // send the event
            EDIT_MODE.next(!!aPayload.editMode);
        }*/

        function _refresh(): void {
            // log
            li(LOGGER, 'refresh');
            // trigger
            wchService.triggerRefresh();
        }

        // construct the message handler
        const _onMessage = createMessageHandler(_getCorsWhitelist, msgHandlers, logger);

        function _destroy() {
            // unregister the message
            if (bHasHandlers) {
                removeEventListener(_MESSAGE_EVENT, _onMessage);
            } else {
                lw(LOGGER, 'removeEventListener not available');
            }
            // delegate
            sdkRouter.destroy();
            sdkJsonp.destroy();
        }

        // init the public APIs
        that.refresh = _refresh;
        that.destroy = _destroy;

        // add the event listener
        if (bHasHandlers) {
            addEventListener(_MESSAGE_EVENT, _onMessage);
        } else {
            lw(LOGGER, 'addEventListener not available');
        }
    }

    get router(): SdkRouter {
        return this.sdkRouter;
    }

    get jsonp(): SdkJsonp {
        return this.sdkJsonp;
    }

    get logger(): LoggerService {
      return this.sdkLogger;
    }

    get search(): SdkSearch {
      return this.sdkSearch;
    }

    /*
     * Returns version information from the SDK
     *
     * @return the version
     */
    get version(): SdkVersion {
        return SDK_VERSION;
    }
}
